import { useState, useEffect } from "react";
import "./style.css";
import PageLayout from "../../component/PageLayout";
import IconUsers from "../../component/SvgIcon/IconUsers";
import SearchBar from "../../component/SearchFilter";
import Pagination from "../../component/Pagination";
import { useFilterPagination } from "../../component/Pagination/useFilterPagination";
import IconShorting from "../../component/SvgIcon/IconShorting";
import api from "../../services";
import { useAuth } from "../../services/AuthContext";
import { useSwal } from "../../services/SweetAlert";
import Swal from "sweetalert2";
import { BootstrapTooltips, DisposeAllTooltips } from "../../services/BootstrapTooltips";
import { formatDateLabel } from "../../services/getLocalDate";

const Tablecolumns = [
    { tableid: "index", header: "#" },
    { tableid: "employee_name", header: "Name" },
    { tableid: "from_date", header: "From" },
    { tableid: "to_date", header: "To" },
    { tableid: "total_days", header: "Days" },
    { tableid: "leave_duration", header: "Leave Duration" },
    { tableid: "reason_description", header: "Reason" },
    { tableid: "approve_or_reject_flag", header: "Approved By" },
    { tableid: "reject_reason", header: "Reject Reason" },
    { tableid: "action", header: "Action" },
    { tableid: "status", header: "Status" },
    { tableid: "edit", header: "Edit" },
];

export default function LeavesStatus() {
    const alert = useSwal();
    const userId = localStorage.getItem("userId");
    const [currentTab, setCurrentTab] = useState("All");
    const [editingRow, setEditingRow] = useState(null);
    const [editData, setEditData] = useState({});
    const { userType: UserType, country: Country } = useAuth();  // ✅ define them here
    // console.log("userType:", UserType, "country:", Country);
    const [leaves, setLeaves] = useState([])

    useEffect(() => {
        api.get("/leaves", { params: { country: Country } }).then((response) => {
            // console.log("Fetched leaves:", response.data);
            setLeaves(response.data.data || []);
        }).catch((error) => {
            console.error("Error fetching leaves:", error);
        });
    }, []);

    // ✅ map tab → API datetype
    const getDateType = (tab) => {
        switch (tab) {
            case "Today":
                return "today";
            case "Future":
                return "upcoming";
            case "Previous":
                return "past";
            default:
                return "";
        }
    };

    // ✅ API call
    const fetchLeaves = (tab) => {
        const datetype = getDateType(tab);

        api.get("/leaves", {
            params: {
                country: Country,
                ...(datetype ? { datetype } : {}),
            },
        })
            .then((response) => {
                console.log("Fetched leaves:", response.data);
                setLeaves(response.data.data || []);
            })
            .catch((error) => {
                console.error("Error fetching leaves:", error);
            });
    };

    // ✅ load data on tab change
    useEffect(() => {
        fetchLeaves(currentTab);
    }, [currentTab]);

    const handleAction = async (leaveItem, newStatus) => {
        try {
            let reason = "";

            // 👉 If rejecting → ask for reason
            if (newStatus === "rejected") {
                const input = await Swal.fire({
                    title: "Reject Leave",
                    input: "textarea",
                    inputLabel: "Enter rejection reason",
                    inputPlaceholder: "Write reason here...",
                    inputAttributes: {
                        'aria-label': 'Write reason here'
                    },
                    showCancelButton: true,
                    confirmButtonText: "Reject",
                    inputValidator: (value) => {
                        if (!value || value.trim() === "") {
                            return "Please enter rejection reason!";
                        }
                    }
                });
                // ❌ only cancel closes
                if (!input.isConfirmed) return;

                reason = input.value;
            }
            // confirmation popup
            const confirm = await alert(
                "question",
                `Are you sure you want to ${newStatus} this leave?`
            );

            if (!confirm.isConfirmed) return;
            await api.post("/leaves/action", {
                leave_id: leaveItem.Id,
                status: newStatus,
                action_by: userId,
                reject_leave_reason: reason,
                // reject_leave_reason_by_team_leader: reason,
            });
            // console.log("leaves/action", res);

            // success toast
            alert("success", `Leave ${newStatus} successfully`);

            // reload data
            fetchLeaves(currentTab);

        } catch (err) {
            console.error("Action failed:", err);
            alert("error", "Something went wrong");
        }
    };

    // ❌ REMOVED TAB FILTERING (backend handles it now)

    const tabFilteredData = leaves.map((item) =>
        Object.fromEntries(
            Object.entries(item).map(([key, val]) => [
                key,
                val === null || val === undefined ? "" : val,
            ])
        )
    );

    // ✅ MAIN HOOK (SEARCH + PAGINATION + SORT)
    const {
        searchQuery,
        setSearchQuery,
        clearSearch,
        page,
        setPage,
        totalPages,
        paginatedData,
        sortConfig,
        setSortConfig,
    } = useFilterPagination(tabFilteredData, {
        defaultPageSize: 20,
    });

    useEffect(() => {
        DisposeAllTooltips();
        BootstrapTooltips();
    }, [paginatedData]);


    const handleUpdate = async () => {
        try {
            await api.post("/leaves/update", {
                ...editData,
                reject_reason: editData.reject_reason,
                updated_by: userId,
            });

            alert("success", "Updated successfully");

            setEditingRow(null);
            setEditData({});
            fetchLeaves(currentTab);

        } catch (err) {
            console.error(err);
            alert("error", "Update failed");
        }
    };
    const handleEdit = (row) => {
        setEditingRow(row.Id);
        setEditData({
            Id: row.Id,
            from_date: row.from_date,
            to_date: row.to_date,
            total_days: row.total_days,
            leave_duration: row.leave_duration,
            reason_description: row.reason_description,

            reject_reason:
                row.reject_leave_reason_by_admin ||
                row.reject_leave_reason_by_team_leader ||
                "",
        });
    };
    const handleCancel = () => {
        setEditingRow(null);
        setEditData({});
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <PageLayout icon={<IconUsers />} pagetitle="Leaves">
            <div className="topspace">
                <div className="container-fluid">
                    <section className="user-section mb-4">

                        {/* Tabs */}
                        <div className="leave-tabs justify-content-between">
                            <div className="tab-nav d-flex position-relative">
                                {["All", "Today", "Future", "Previous"].map(
                                    (tab) => (
                                        <button
                                            key={tab}
                                            className={`tab-btn ${currentTab === tab
                                                ? "active"
                                                : ""
                                                }`}
                                            onClick={() => setCurrentTab(tab)}
                                        >
                                            {tab}
                                        </button>
                                    )
                                )}
                                <span
                                    className={`tab-indicator ${currentTab}`}
                                />
                            </div>

                            {/* Search + Pagination */}
                            <div className="datafilter d-flex gap-2 align-items-center">
                                <SearchBar
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    onClear={clearSearch}
                                />
                                <Pagination
                                    currentPage={page}
                                    totalPages={totalPages}
                                    onPageChange={setPage}
                                />
                            </div>
                        </div>

                        {/* TABLE */}
                        <div className="table-responsive">
                            <table className="table">
                                <thead className="table-light">
                                    <tr>
                                        {Tablecolumns.map((col, i) => (
                                            <th
                                                key={i}
                                                className="cursor-pointer"
                                                onClick={() => {
                                                    if (
                                                        col.tableid ===
                                                        "index"
                                                    )
                                                        return;

                                                    setSortConfig((prev) => ({
                                                        key: col.tableid,
                                                        direction:
                                                            prev.key ===
                                                                col.tableid &&
                                                                prev.direction ===
                                                                "asc"
                                                                ? "desc"
                                                                : "asc",
                                                    }));
                                                }}
                                            >
                                                {col.header}{" "}
                                                {col.tableid !== "index" && (
                                                    <IconShorting
                                                        activeDirection={
                                                            sortConfig.key ===
                                                                col.tableid
                                                                ? sortConfig.direction
                                                                : null
                                                        }
                                                    />
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {paginatedData.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan="9"
                                                className="text-center"
                                            >
                                                No records found
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedData.map((l, i) => (
                                            <tr key={i}>
                                                <td>{i + 1}</td>
                                                <td>{l.employee_name}</td>
                                                <td>{editingRow === l.Id ? (
                                                    <input
                                                        type="date"
                                                        name="from_date"
                                                        value={editData.from_date || ""}
                                                        onChange={handleChange}
                                                        className="form-control date-input"
                                                    />
                                                ) : (
                                                    formatDateLabel(l.from_date)
                                                )}</td>
                                                <td>
                                                    {editingRow === l.Id ? (
                                                        <input
                                                            type="date"
                                                            name="to_date"
                                                            value={editData.to_date || ""}
                                                            onChange={handleChange}
                                                            className="form-control date-input"
                                                        />
                                                    ) : (
                                                        formatDateLabel(l.to_date)
                                                    )}</td>

                                                <td>
                                                    {editingRow === l.Id ? (
                                                        <input
                                                            type="text"
                                                            name="total_days"
                                                            value={editData.total_days || ""}
                                                            onChange={handleChange}
                                                            className="form-control"
                                                        />
                                                    ) : (
                                                        (l.total_days)
                                                    )}</td>
                                                <td>
                                                    {editingRow === l.Id ? (
                                                        <input
                                                            type="text"
                                                            name="leave_duration"
                                                            value={editData.leave_duration || ""}
                                                            onChange={handleChange}
                                                            className="form-control"
                                                        />
                                                    ) : (
                                                        (l.leave_duration)
                                                    )}</td>
                                                <td>
                                                    {editingRow === l.Id ? (
                                                        <input
                                                            type="text"
                                                            name="reason_description"
                                                            value={editData.reason_description || ""}
                                                            onChange={handleChange}
                                                            className="form-control"
                                                        />
                                                    ) : (
                                                        (() => {
                                                            const text = l.reason_description || "";
                                                            const words = text.trim().split(/\s+/).length;

                                                            if (text && words > 3) {
                                                                return (
                                                                    <span
                                                                        title={text}
                                                                        className="text-truncate d-inline-block"
                                                                        style={{ maxWidth: "150px", cursor: "pointer" }}
                                                                    >
                                                                        {text.split(" ").slice(0, 3).join(" ")}...
                                                                    </span>
                                                                );
                                                            }
                                                            return text || "-";
                                                        })()
                                                    )}
                                                </td>
                                                <td>
                                                    {l.updated_by_name || "-"}
                                                </td>

                                                <td>
                                                    {editingRow === l.Id ? (
                                                        <input
                                                            type="text"
                                                            name="reject_reason"
                                                            value={editData.reject_reason || ""}
                                                            onChange={handleChange}
                                                            className="form-control"
                                                        />
                                                    ) : (
                                                        (() => {
                                                            const text =
                                                                l.reject_leave_reason_by_admin ||
                                                                l.reject_leave_reason_by_team_leader ||
                                                                "";

                                                            const words = text.trim().split(/\s+/).length;

                                                            if (text && words > 3) {
                                                                return (
                                                                    <span
                                                                        title={text}
                                                                        className="text-truncate d-inline-block"
                                                                        style={{ maxWidth: "150px", cursor: "pointer" }}
                                                                    >
                                                                        {text.split(" ").slice(0, 3).join(" ")}...
                                                                    </span>
                                                                );
                                                            }
                                                            return text || "-";
                                                        })()
                                                    )}
                                                </td>



                                                <td>
                                                    {l.approve_or_reject_flag ===
                                                        "pending" ||
                                                        l.approve_or_reject_flag ===
                                                        "Pending" ? (
                                                        <div className="d-flex gap-1 table-action-btn">
                                                            <button
                                                                className="btn btn-success py-1 px-2"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAction(
                                                                        l,
                                                                        "approved"
                                                                    );
                                                                }}
                                                            >
                                                                Approve
                                                            </button>
                                                            <button
                                                                className="btn btn-danger py-1 px-2"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAction(
                                                                        l,
                                                                        "rejected"
                                                                    );
                                                                }}
                                                            >
                                                                Reject
                                                            </button>
                                                        </div>
                                                    ) : l.approve_or_reject_flag ===
                                                        "approved" ||
                                                        l.approve_or_reject_flag ===
                                                        "approve" ? (
                                                        <div className="table-action-btn">
                                                            <button
                                                                className="btn btn-cancel py-1 px-2"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAction(
                                                                        l,
                                                                        "pending"
                                                                    );
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">
                                                            -
                                                        </span>
                                                    )}
                                                </td>

                                                <td>
                                                    <span
                                                        className={`badge status-${l.approve_or_reject_flag?.toLowerCase()}`}
                                                    >
                                                        {l.approve_or_reject_flag
                                                            ? l.approve_or_reject_flag
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                            l.approve_or_reject_flag.slice(
                                                                1
                                                            )
                                                            : ""}
                                                    </span>
                                                </td>
                                                <td>
                                                    {editingRow === l.Id ? (
                                                        <div className="d-flex gap-1 table-action-btn">
                                                            <button className="btn btn-success py-1 px-2" onClick={handleUpdate}>
                                                                Save
                                                            </button>
                                                            <button className="btn btn-danger py-1 px-2" onClick={handleCancel}>
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="d-flex gap-1 table-action-btn">
                                                            <button className="btn btn-success py-1 px-2" onClick={() => handleEdit(l)}>
                                                                Edit
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            </div>
        </PageLayout>
    );
}